import { ForwardedRef, forwardRef, RefObject, useRef } from 'react';

import Focus from '@tiptap/extension-focus';
import styled from 'styled-components';

import { useAiInputAssistModal } from '@components/ai-input-assist/ai-input-modal';
import { AiInputProps } from '@components/ai-input-assist/types';
import { IconButton } from '@components/icon-button';
import { IconAddSmall } from '@components/icons/add-small';
import { IconAI } from '@components/icons/ai';
import { hideModal } from '@components/modal';
import {
  hideExtendedSidebar,
  showExtendedSidebar,
  useExtendedSidebarData,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { classNames } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import {
  FieldMappingData,
  FieldMappingEditorValue,
  FieldMappingNode,
  FieldMappingVariables,
  FieldType,
  transformInput,
  transformOutput,
} from './addons/field-mapping';
import { FieldMappingSidebar, FieldMappingSidebarProps } from './field-mapping-sidebar';
import { FieldMappingContext } from './helpers';
import { BaseTextEditorProps, EditorMethods, TextEditor } from './text-editor';

export type BaseFieldMappingTextEditorProps = BaseTextEditorProps<{
  variables?: FieldMappingVariables;
  actions?: JSX.Element;
}> &
  FieldMappingData & { aiOptions?: AiInputProps };

export type FieldMappingTextEditorProps = BaseFieldMappingTextEditorProps & {
  defaultValue?: FieldMappingEditorValue;
  fieldType?: FieldType;
  shouldShowFieldMappingOnFocus?: boolean;
  onManualSelect?: (value: any) => void;
  manualData?: any;
  shouldHideMenu?: boolean;
};

const getActionsClassName = (editorUuid: string) => {
  return `field-mapping-actions-${editorUuid}`;
};

export const FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME = 'field-mapping-text-editor';

export const FieldMappingTextEditor = forwardRef(
  (
    {
      defaultValue,
      onChange,
      variableGroups,
      extensions,
      actions,
      aiOptions,
      fieldMappingId,
      fieldType = 'text',
      shouldHideMenu = false,
      shouldShowFieldMappingOnFocus = false,
      onManualSelect,
      manualData,
      className,
      ...rest
    }: FieldMappingTextEditorProps,
    ref: ForwardedRef<EditorMethods>
  ) => {
    const editorUuid = useRef(uuid()).current;
    const { isExtended, extendedId } = useExtendedSidebarData();
    const editorRef = useRef<EditorMethods>(null);
    const finalRef = (ref ?? editorRef) as RefObject<EditorMethods>;
    const showAiInputAssistModal = useAiInputAssistModal();
    const parsedDefaultValue = defaultValue
      ? transformInput({ ...defaultValue, variableGroups })
      : undefined;
    const handleChange = (value: string) => {
      onChange?.(transformOutput(value));
    };

    const showFieldMappingSidebar = (props: Pick<FieldMappingSidebarProps, 'variable'> = {}) => {
      showExtendedSidebar({
        component: () => {
          return (
            <FieldMappingSidebar
              editorRef={finalRef}
              variableGroups={variableGroups ?? []}
              fieldType={fieldType}
              label={rest.label}
              fieldMappingId={fieldMappingId ?? ''}
              onManualSelect={onManualSelect}
              manualData={manualData}
              shouldHideMenu={shouldHideMenu}
              {...props}
            />
          );
        },
        extendedId: editorUuid,
      });
    };
    const actionsId = getActionsClassName(editorUuid);
    const isThisOneExtended = isExtended && extendedId === editorUuid;

    const Actions = actions ?? (
      <Stack orientation="horizontal" space={2} id={actionsId}>
        {aiOptions && (
          <IconButton
            icon={<IconAI size={16} />}
            size="sm"
            variant="aiGradient-white"
            onClick={() =>
              showAiInputAssistModal({
                ...aiOptions,
                plaintext: rest.plaintext,
                editorUuid,
                variableGroups,
                onSave: (value) => {
                  finalRef.current?.replaceContent(value);
                  handleChange(value);
                  hideModal();
                },
              })
            }
          />
        )}
        {variableGroups && (
          <IconButton
            icon={<IconAddSmall />}
            variant="blue-white"
            size="sm"
            onClick={() => {
              !isExtended || (isExtended && extendedId !== editorUuid)
                ? showFieldMappingSidebar()
                : hideExtendedSidebar();
            }}
          />
        )}
      </Stack>
    );

    return (
      <FieldMappingContext.Provider
        value={{ showFieldMappingSidebar, variableGroups: variableGroups ?? [] }}
      >
        <StyledEditor
          isActive={isThisOneExtended}
          key={variableGroups?.length}
          className={classNames(FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME, className)}
          defaultValue={parsedDefaultValue}
          extensions={[
            FieldMappingNode,
            Focus.configure({ mode: 'deepest' }),
            ...(extensions ?? []),
          ]}
          ref={finalRef}
          actions={Actions}
          onChange={(data) => handleChange(data.value)}
          onFocus={() => {
            const actionsElement = document.querySelector(`#${actionsId}`);
            if (actionsElement && actionsElement.contains(document.activeElement)) {
              return;
            }
            if (
              (isExtended || shouldShowFieldMappingOnFocus) &&
              variableGroups &&
              extendedId !== editorUuid
            ) {
              showFieldMappingSidebar();
            }
          }}
          {...rest}
        />
      </FieldMappingContext.Provider>
    );
  }
);

const StyledEditor = styled(TextEditor)({
  '.ProseMirror-hideselection .has-focus': {
    '.placeholder-container': {
      filter: 'contrast(92%)',
    },
  },
  ol: {
    margin: 0,
    marginBottom: 5,
    paddingInlineStart: 25,
  },
  ul: {
    margin: 0,
    marginBottom: 5,
    paddingInlineStart: 25,
  },
  '.placeholder-container': {
    // Dirty-hax. 22 instead of 24 because the line-height of the text box is 24
    // and if the placeholder is 24px it will end up overflowing a tiny bit.
    // So we make it take up 22px of 'space' but then it overflows so its displayed
    // correctly. The -2 margin top is to align the pill better on the line.
    height: pxToRem(22),
    marginTop: pxToRem(-2),
  },
});

export const NodeStyledFieldMappingTextEditor = styled(FieldMappingTextEditor)({
  cursor: 'pointer',
  '.node-wrapper': {
    marginRight: pxToRem(8),
  },
});
